// FirewallLab.js
import React, { useEffect, useRef, useState } from "react";
import "./FirewallLab.css";

const API_URL =
  "https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection";

async function callApi(payload) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ method: "firewall", ...payload }),
  });
  const text = await res.text();
  console.log("RAW RESPONSE:", text);

  let outer;
  try {
    outer = JSON.parse(text);
  } catch {
    throw new Error("Backend did not return JSON: " + text);
  }
  let inner;
  if (typeof outer.body === "string") {
    try {
      inner = JSON.parse(outer.body);
    } catch {
      inner = { error: outer.body }; // fallback to plain string
    }
  } else {
    inner = outer.body || outer;
  }

  if (outer.statusCode && outer.statusCode >= 400) {
    throw new Error(
      inner.error || inner.message || outer.body || "Request failed"
    );
  }

  return inner;
}

function RuleRow({ rule, index, onChange, onDelete, onMoveUp, onMoveDown }) {
  const update = (k, v) => onChange(index, { ...rule, [k]: v });

  return (
    <div className="rule-row">
      <div className="rule-cell small">
        <input
          type="checkbox"
          checked={!!rule.enabled}
          onChange={(e) => update("enabled", e.target.checked)}
        />
      </div>
      <div className="rule-cell">
        <select
          value={rule.action}
          onChange={(e) => update("action", e.target.value)}
        >
          <option value="allow">ALLOW</option>
          <option value="deny">DENY</option>
        </select>
      </div>
      <div className="rule-cell">
        <select
          value={rule.direction}
          onChange={(e) => update("direction", e.target.value)}
        >
          <option value="ingress">ingress</option>
          <option value="egress">egress</option>
          <option value="any">any</option>
        </select>
      </div>
      <div className="rule-cell">
        <select
          value={rule.protocol}
          onChange={(e) => update("protocol", e.target.value)}
        >
          <option>ANY</option>
          <option>TCP</option>
          <option>UDP</option>
          <option>ICMP</option>
        </select>
      </div>
      <div className="rule-cell">
        <input
          value={rule.src}
          onChange={(e) => update("src", e.target.value)}
          placeholder="src CIDR (any)"
        />
      </div>
      <div className="rule-cell">
        <input
          value={rule.dst}
          onChange={(e) => update("dst", e.target.value)}
          placeholder="dst CIDR (any)"
        />
      </div>
      <div className="rule-cell">
        <input
          value={rule.ports}
          onChange={(e) => update("ports", e.target.value)}
          placeholder="ports ex: 80,443 or 1000-2000"
        />
      </div>
      <div className="rule-cell grow">
        <input
          value={rule.description}
          onChange={(e) => update("description", e.target.value)}
          placeholder="description"
        />
      </div>
      <div className="rule-cell actions">
        <button
          className="ghost"
          onClick={() => onMoveUp(index)}
          title="Move up"
        >
          ↑
        </button>
        <button
          className="ghost"
          onClick={() => onMoveDown(index)}
          title="Move down"
        >
          ↓
        </button>
        <button
          className="danger"
          onClick={() => onDelete(index)}
          title="Delete"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default function FirewallLab() {
    const [terminal, setTerminal] = useState("");
    const [scenario, setScenario] = useState("web_server");
    const [defaultPolicy, setDefaultPolicy] = useState("deny");
    const [rules, setRules] = useState([]);
    const [session, setSession] = useState(null);
    const [packets, setPackets] = useState([]);
    const [summary, setSummary] = useState({
      allowed: 0,
      blocked: 0,
      per_service: {},
    });
    const [isRunning, setIsRunning] = useState(false);
  
    const tickTimer = useRef(null);
  
    const appendTerm = (msg) => setTerminal((t) => t + msg);
  
    // --- API ---
    const init = async () => {
      try {
        const data = await callApi({
          action: "init",
          scenario,
          default_policy: defaultPolicy,
          session,
        });
        appendTerm(data.terminal || "");
        setSession(data.session);
      } catch (e) {
        appendTerm(`[!] Error: ${e.message}\n`);
      }
    };
  
    const step = async () => {
      try {
        const data = await callApi({
          action: "tick",
          scenario,
          default_policy: defaultPolicy,
          rules,
          session,
        });
        appendTerm(data.terminal || "");
        setSession(data.session);
        setPackets((p) => {
          const next = [...data.packets, ...p].slice(0, 120);
          return next;
        });
        setSummary(data.summary || { allowed: 0, blocked: 0, per_service: {} });
      } catch (e) {
        appendTerm(`[!] Error: ${e.message}\n`);
        setIsRunning(false);
        clearInterval(tickTimer.current);
      }
    };
  
    useEffect(() => {
      init();
    }, []);
  
    // --- Controls ---
    const start = () => {
      if (isRunning) return;
      setIsRunning(true);
      step();
      tickTimer.current = setInterval(() => step(), 1400);
    };
  
    const stop = () => {
      setIsRunning(false);
      clearInterval(tickTimer.current);
    };
  
    const reset = () => {
      stop();
      setTerminal("");
      setRules([]);
      setPackets([]);
      setSummary({ allowed: 0, blocked: 0, per_service: {} });
      setSession(null);
      init();
    };
  
    // --- Rules editing ---
    const addRule = (r) => setRules([...rules, r]);
    const changeRule = (idx, r) =>
      setRules(rules.map((x, i) => (i === idx ? r : x)));
    const deleteRule = (idx) => setRules(rules.filter((_, i) => i !== idx));
    const moveUp = (idx) => {
      if (idx === 0) return;
      const next = [...rules];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      setRules(next);
    };
    const moveDown = (idx) => {
      if (idx === rules.length - 1) return;
      const next = [...rules];
      [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
      setRules(next);
    };
  
    const addTemplate = (name) => {
      if (name === "allow_web") {
        addRule({
          enabled: true,
          action: "allow",
          direction: "ingress",
          protocol: "TCP",
          src: "any",
          dst: "10.0.0.10/32",
          ports: "80,443",
          description: "Allow web",
        });
      } else if (name === "deny_ssh") {
        addRule({
          enabled: true,
          action: "deny",
          direction: "ingress",
          protocol: "TCP",
          src: "any",
          dst: "10.0.0.10/32",
          ports: "22",
          description: "Block SSH brute force",
        });
      } else if (name === "allow_dns_ntp") {
        addRule({
          enabled: true,
          action: "allow",
          direction: "egress",
          protocol: "UDP",
          src: "10.0.0.10/32",
          dst: "any",
          ports: "53,123",
          description: "Allow DNS/NTP out",
        });
      }
    };
  
    return (
      <div className="fw-container">
        <h1 className="fw-title">🛡️ Firewall Rules Simulation</h1>
        <p className="fw-sub">
          Add ALLOW/DENY rules, step the traffic, and observe what passes or gets
          blocked.
        </p>
  
        <div className="fw-layout">
          {/* Left: Controls + Rules */}
          <div className="fw-left">
            <div className="controls">
              <div className="row">
                <label>Scenario</label>
                <select
                  value={scenario}
                  onChange={(e) => setScenario(e.target.value)}
                  disabled={isRunning}
                >
                  <option value="web_server">
                    Web Server (HTTP/HTTPS + SSH noise)
                  </option>
                  <option value="db_server">Database Server</option>
                  <option value="workstation">Corporate Workstation</option>
                  <option value="mail_server">Mail Server</option>
                  <option value="dns_resolver">DNS Resolver</option>
                  <option value="iot_device">IoT Device</option>
                  <option value="dmz_app">DMZ Application Server</option>
                </select>
              </div>
              <div className="row">
                <label>Default Policy</label>
                <select
                  value={defaultPolicy}
                  onChange={(e) => setDefaultPolicy(e.target.value)}
                  disabled={isRunning}
                >
                  <option value="deny">DENY (recommended)</option>
                  <option value="allow">ALLOW</option>
                </select>
              </div>
              <div className="row buttons">
                {isRunning ? (
                  <button onClick={stop}>Stop</button>
                ) : (
                  <button onClick={start}>Start</button>
                )}
                <button onClick={step} disabled={isRunning}>
                  Step
                </button>
                <button className="ghost" onClick={reset}>
                  Reset
                </button>
              </div>
            </div>
  
            <div className="rules">
              <div className="rules-header">
                <h3>Rules (top = highest priority)</h3>
                <div className="templates">
                  <span>Templates:</span>
                  <button
                    className="tiny"
                    onClick={() => addTemplate("allow_web")}
                  >
                    Allow Web
                  </button>
                  <button
                    className="tiny"
                    onClick={() => addTemplate("deny_ssh")}
                  >
                    Deny SSH
                  </button>
                  <button
                    className="tiny"
                    onClick={() => addTemplate("allow_dns_ntp")}
                  >
                    Allow DNS/NTP
                  </button>
                </div>
                <button
                  className="add"
                  onClick={() =>
                    addRule({
                      enabled: true,
                      action: "deny",
                      direction: "ingress",
                      protocol: "ANY",
                      src: "any",
                      dst: "10.0.0.10/32",
                      ports: "any",
                      description: "",
                    })
                  }
                >
                  + Add Rule
                </button>
              </div>
  
              <div className="rule-table">
                <div className="rule-header">
                  <div className="rule-cell small">On</div>
                  <div className="rule-cell">Action</div>
                  <div className="rule-cell">Direction</div>
                  <div className="rule-cell">Protocol</div>
                  <div className="rule-cell">Src CIDR</div>
                  <div className="rule-cell">Dst CIDR</div>
                  <div className="rule-cell">Ports</div>
                  <div className="rule-cell grow">Description</div>
                  <div className="rule-cell actions">Actions</div>
                </div>
  
                {rules.length === 0 && (
                  <div className="rule-empty">
                    No rules yet. Add one or use a template.
                  </div>
                )}
  
                {rules.map((r, idx) => (
                  <RuleRow
                    key={idx}
                    rule={r}
                    index={idx}
                    onChange={changeRule}
                    onDelete={deleteRule}
                    onMoveUp={moveUp}
                    onMoveDown={moveDown}
                  />
                ))}
              </div>
            </div>
          </div>
  
          {/* Right: Terminal + Stats */}
          <div className="fw-right">
            <div className="terminal-box">
              <h3>Terminal</h3>
              <pre className="terminal">{terminal || "Ready.\n"}</pre>
            </div>
            <div className="stats">
              <div className="stat allowed">Allowed: {summary.allowed}</div>
              <div className="stat blocked">Blocked: {summary.blocked}</div>
            </div>
          </div>
        </div>
  
        {/* Bottom: Packet Table */}
        <div className="fw-bottom">
          <div className="packet-list">
            <div className="packet-header">
              <div>Result</div>
              <div>Dir</div>
              <div>Proto</div>
              <div>Port</div>
              <div>Service</div>
              <div>Flow</div>
              <div>Rule</div>
            </div>
            {packets.map((p, i) => (
              <div key={i} className={`packet-row ${p.allowed ? "ok" : "bad"}`}>
                <div>{p.allowed ? "ALLOW" : "DENY"}</div>
                <div>{p.dir}</div>
                <div>{p.protocol}</div>
                <div>{p.dst_port || "-"}</div>
                <div>{p.service}</div>
                <div className="flow">
                  {p.src_ip} ➜ {p.dst_ip}
                </div>
                <div>
                  {p.matched_rule !== null && p.matched_rule !== undefined
                    ? `#${p.matched_rule + 1}`
                    : "-"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
